import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import InlineSVG from 'react-inlinesvg';

import gbcrImage from 'images/gcbr_mono.svg';
import wordmarkImage from 'images/logo/logo.svg';

import BugButton from 'components/Button/BugButton';
import useEditContext from 'components/edit/EditContext';
import LicenseIcon from 'components/icons/License';
import Link from 'components/links/Link';
import FeedbackButton from 'components/shared/FeedbackButton';
import Svg from 'components/shared/Svg';

import './footer.scss';

/**
 * Footer that appears on every page of the site
 *
 * @return {node} footer nav element
 */
export default function Footer({}) {
  const editContext = useEditContext();
  const logo = <Svg className="pageFooter__wordmark" src={wordmarkImage} ariaLabel="ClinPGx" />;
  const FooterButton = editContext.isEditMode ? BugButton : FeedbackButton;
  const fullYear = new Date().getFullYear();

  return (
    <footer>
      <div className="pageFooter">
        <nav className="container" aria-label="Footer">
          <div className="row d-print-none">
            <div className="col-md-3 col-lg-3">
              <a href="/" className="brand brand__clinpgx" aria-label="ClinPGx logo">
                {logo}
              </a>
            </div>

            <div className="col-md-3 col-lg-2">
              <a href="/page/overview" className="pageFooter-link">About ClinPGx</a>
              <a href="/acknowledgements" className="pageFooter-link">Acknowledgements</a>
              <a id="faq" href="/page/faqs" className="pageFooter-link">FAQ</a>
            </div>
            <div className="col-md-3 col-lg-2">
              <a href="/publications" className="pageFooter-link">Publications</a>
              <a href="/downloads" className="pageFooter-link">Downloads</a>
              <a href="/page/citingClinpgx" className="pageFooter-link">Citing</a>
            </div>
            <div className="col-md-3 col-lg-3">
              <a href="/page/dataUsagePolicy" className="pageFooter-link">Licensing &amp; Usage</a>
              <a href="/page/privacyPolicy" className="pageFooter-link">Privacy Policy</a>
            </div>

            <div className="col-md-3 col-lg-2">
              <div className="pageFooter__social">
                <a
                  href="https://www.youtube.com/@ClinPGx"
                  title="ClinPGx @ YouTube"
                  className="fa-layers fa-fw"
                >
                  <FontAwesomeIcon icon="square" size="2x" className="icon_bg" />
                  <FontAwesomeIcon icon={['fab', 'youtube']} size="lg" color="#FF0000" />
                  <span className="screen-reader-text">ClinPGx @ YouTube</span>
                </a>
                <a
                  href="https://bsky.app/profile/clinpgx.org"
                  title="ClinPGx @ Bluesky"
                  className="fa-layers fa-fw"
                >
                  <FontAwesomeIcon icon="square" size="2x" className="icon_bg" />
                  <FontAwesomeIcon icon={['fab', 'bluesky']} size="lg" color="#0a7aff" />
                  <span className="screen-reader-text">ClinPGx @ Bluesky</span>
                </a>
                <a
                  href="https://www.linkedin.com/company/ClinPGx"
                  title="ClinPGx @ LinkedIn"
                  className="fa-layers fa-fw"
                >
                  <FontAwesomeIcon icon="square" size="2x" className="icon_bg" />
                  <FontAwesomeIcon icon={['fab', 'linkedin-in']} size="lg" color="#0966C2" />
                  <span className="screen-reader-text">ClinPGx @ LinkedIn</span>
                </a>
                <a
                  href="https://blog.clinpgx.org"
                  title="ClinPGx Blog"
                  className="fa-layers fa-fw"
                >
                  <FontAwesomeIcon icon="square" size="2x" className="icon_bg" />
                  <FontAwesomeIcon icon="rss" size="lg" color="#ee802f" />
                  <span className="screen-reader-text">ClinPGx Blog</span>
                </a>
              </div>
              <div className="pageFooter__gcbr">
                <Link href="https://globalbiodata.org/scientific-activities/global-core-biodata-resources/">
                  <InlineSVG src={gbcrImage} title="GBCR Logo" className="p-0" />
                </Link>
              </div>
            </div>
          </div>
          <div className="colophon">
            <div className="colophon__content">
              <p className="font-italic">
                The information on this website is not intended for direct diagnostic use or medical
                decision-making without review by a health care professional. Individuals should not
                change their health behavior solely on the basis of information contained on this
                website. If you have questions about the information contained on this website, please
                see a health care professional.
              </p>
              <p>
                ClinPGx is comprised of PharmGKB<sup>&reg;</sup>, CPIC<sup>&copy;</sup>, and PharmCAT.

                PharmGKB is a registered trademark of HHS and is financially supported by NIH/NHGRI/NICHD/NIDA/NCI.
                PharmGKB is managed at Stanford University (U24HG010615).
                &copy;2001-{fullYear}.

                CPIC is a registered service mark of the US Department of Health & Human Services (HHS).
                CPIC is managed at Stanford University & St. Jude Children&apos;s Research Hospital
                (U24HG013077).

                PharmCAT is managed at Stanford University & University of Pennsylvania (NHGRI U24HG013077).
              </p>
              <p>
                ClinPGx and PharmGKB<sup>&reg;</sup> data are subject to the
                Creative Commons Attribution-ShareAlike 4.0 license. <LicenseIcon compact={true} />
              </p>
            </div>
          </div>
        </nav>
      </div>
      <FooterButton />
    </footer>
  );
}
